import type {
  CarProject,
  ChatSession,
  InviteCreateResult,
  KnownCar,
  User,
} from "../api/client";
import type { ChatMessage } from "../state/chat";
import type { CanvasPanel } from "../state/canvas";
import { AdminInvitePanel } from "./AdminInvitePanel";
import { CanvasView } from "./CanvasView";
import { CarProjectList } from "./CarProjectList";
import { AddCarDialog } from "./AddCarDialog";
import { Composer } from "./Composer";
import { MessageList } from "./MessageList";

interface ChatLayoutProps {
  user: User;
  appName: string;
  projects: CarProject[];
  sessions: ChatSession[];
  activeCarSlug: string | null;
  activeSession: ChatSession | null;
  activeProjectName: string;
  messages: ChatMessage[];
  drawerOpen: boolean;
  canvasOpen: boolean;
  canvasPanels: CanvasPanel[];
  addCarOpen: boolean;
  knownCars: KnownCar[];
  knownCarsLoading: boolean;
  knownCarsError: string | null;
  projectsLoading: boolean;
  sessionsLoading: boolean;
  messagesLoading: boolean;
  isStreaming: boolean;
  error: string | null;
  onDrawerOpen: () => void;
  onDrawerClose: () => void;
  onCanvasOpen: () => void;
  onCanvasClose: () => void;
  onSelectProject: (carSlug: string | null) => void;
  onSessionSelect: (sessionId: string) => void;
  onCreateSessionForCar: (carSlug: string | null) => void;
  onDeleteSession: (sessionId: string) => void;
  onAddCarOpen: () => void;
  onAddCarClose: () => void;
  onPickCar: (car: KnownCar) => void;
  onSend: (text: string) => Promise<void>;
  onCreateInvite: (email: string) => Promise<InviteCreateResult>;
  onStop: () => void;
  onLogout: () => void;
}

export function ChatLayout({
  user,
  appName,
  projects,
  sessions,
  activeCarSlug,
  activeSession,
  activeProjectName,
  messages,
  drawerOpen,
  canvasOpen,
  canvasPanels,
  addCarOpen,
  knownCars,
  knownCarsLoading,
  knownCarsError,
  projectsLoading,
  sessionsLoading,
  messagesLoading,
  isStreaming,
  error,
  onDrawerOpen,
  onDrawerClose,
  onCanvasOpen,
  onCanvasClose,
  onSelectProject,
  onSessionSelect,
  onCreateSessionForCar,
  onDeleteSession,
  onAddCarOpen,
  onAddCarClose,
  onPickCar,
  onSend,
  onCreateInvite,
  onStop,
  onLogout,
}: ChatLayoutProps) {
  const existingSlugs = new Set(
    projects.map((p) => p.car_slug).filter((slug): slug is string => Boolean(slug)),
  );

  return (
    <main className={`chat-shell ${canvasOpen ? "canvas-open" : ""}`}>
      <CarProjectList
        projects={projects}
        sessions={sessions}
        activeCarSlug={activeCarSlug}
        activeSessionId={activeSession?.id ?? null}
        isOpen={drawerOpen}
        isLoading={projectsLoading || sessionsLoading}
        onClose={onDrawerClose}
        onSelectProject={onSelectProject}
        onSelectSession={onSessionSelect}
        onCreateSessionForCar={onCreateSessionForCar}
        onDeleteSession={onDeleteSession}
        onAddCar={onAddCarOpen}
      />

      <section className="chat-main">
        <header className="top-bar">
          <button className="icon-button mobile-menu" type="button" onClick={onDrawerOpen}>
            项目
          </button>
          <div className="top-title">
            <p className="breadcrumb">
              <span>{appName || "车大师"}</span>
              <span className="crumb-sep">/</span>
              <span>{activeProjectName}</span>
              <span className="crumb-sep">/</span>
              <span>{activeSession?.title || "新对话"}</span>
            </p>
            <h1>{activeSession?.title || "新对话"}</h1>
          </div>
          <div className={`stream-status ${isStreaming ? "active" : ""}`}>
            <span />
            {isStreaming ? "生成中" : "就绪"}
          </div>
          <button
            className="icon-button canvas-toggle"
            type="button"
            onClick={canvasOpen ? onCanvasClose : onCanvasOpen}
          >
            {canvasOpen ? "隐藏视图" : "系统视图"}
          </button>
          {user.is_admin ? <AdminInvitePanel onCreateInvite={onCreateInvite} /> : null}
          <button className="icon-button" type="button" onClick={onLogout}>
            退出
          </button>
        </header>

        {error ? <div className="inline-error">{error}</div> : null}

        <MessageList messages={messages} isLoading={messagesLoading} />

        <footer className="composer-wrap">
          <div className="composer-meta">
            <span>{user.display_name || user.email}</span>
            <span>Enter 发送，Shift + Enter 换行</span>
          </div>
          <Composer
            // Also gate on sessionsLoading so the composer disables the moment
            // pickCar / createSession starts. Otherwise a too-fast user can
            // fire a prompt into the previous session while the new one is
            // still being created — the stream then lands on the old session
            // and the new session's mount wipes the optimistic insert.
            disabled={!activeSession || isStreaming || sessionsLoading}
            isStreaming={isStreaming}
            onSend={onSend}
            onStop={onStop}
          />
        </footer>
      </section>

      <CanvasView
        panels={canvasPanels}
        isOpen={canvasOpen}
        onClose={onCanvasClose}
      />

      {canvasOpen ? (
        <button
          className="canvas-scrim"
          type="button"
          aria-label="关闭数据视图"
          onClick={onCanvasClose}
        />
      ) : null}

      <AddCarDialog
        open={addCarOpen}
        isLoading={knownCarsLoading}
        knownCars={knownCars}
        existingSlugs={existingSlugs}
        error={knownCarsError}
        onClose={onAddCarClose}
        onPick={onPickCar}
      />
    </main>
  );
}
